import React, { useCallback, useEffect, useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { SectionHeading } from './ui';
type Bonus = {
  title: string;
  pages: string;
  value: string;
  icon: string;
  img?: string;
};
const bonuses: Bonus[] = [{
  title: 'TRAZOS MAESTROS',
  pages: '17 páginas',
  value: '$4.999',
  icon: '✏️',
  img: 'https://cdn.shopify.com/s/files/1/0999/7487/3381/files/Punteado_y_lineas.png?v=1780592902'
}, {
  title: 'MUNDO DE FORMAS',
  pages: '23 páginas',
  value: '$4.999',
  icon: '🎨',
  img: 'https://cdn.shopify.com/s/files/1/0999/7487/3381/files/Colores_y_Formas.png?v=1780592903'
}, {
  title: 'SUPER COLOREA',
  pages: '82 páginas',
  value: '$9.999',
  icon: '🖍',
  img: 'https://cdn.shopify.com/s/files/1/0999/7487/3381/files/Coloreando_80.png?v=1780592902'
}, {
  title: 'MEGA JUEGOS EDUCATIVOS',
  pages: '57 páginas',
  value: '$9.999',
  icon: '🧩',
  img: 'https://cdn.shopify.com/s/files/1/0999/7487/3381/files/Cuadernillo_de_juegos.png?v=1780592903'
}, {
  title: 'BIBLIOTECA DE CUENTITOS',
  pages: '75 páginas',
  value: '$8.999',
  icon: '📚',
  img: 'https://cdn.shopify.com/s/files/1/0999/7487/3381/files/Cuadernillo_de_lecturas.png?v=1780592904'
}, {
  title: 'DIPLOMA ESTRELLA',
  pages: 'Imprimible',
  value: '$3.999',
  icon: '🏆',
  img: 'https://cdn.shopify.com/s/files/1/0999/7487/3381/files/Diploma.png?v=1780661522'
}];
const BonusCard = ({
  bono,
  index
}: {
  bono: Bonus;
  index: number;
}) => <div className="bg-white rounded-3xl shadow-soft border-2 border-brand-yellow/20 relative overflow-hidden group hover:border-brand-yellow transition-colors h-full flex flex-col" data-id="element-40">
    {/* Gratis Badge */}
    <div className="absolute top-6 -right-10 bg-brand-pink text-white font-bold py-1 px-10 transform rotate-45 shadow-md z-10" data-id="element-41">
      🎁 GRATIS
    </div>

    {/* Image / visual header */}
    <div className="relative h-48 bg-gradient-to-br from-brand-yellow/30 via-brand-pink/10 to-brand-blue/20 flex items-center justify-center overflow-hidden" data-id="element-42">
      {bono.img ? <img src={`${bono.img}&width=600&quality=80&format=webp`} alt={bono.title} width="600" height="400" loading="lazy" decoding="async" className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-500" data-id="element-43" /> : <span className="text-7xl transform group-hover:scale-110 transition-transform" data-id="element-44">
          {bono.icon}
        </span>}
      {bono.img && <div className="absolute bottom-3 left-3 text-3xl drop-shadow-md" data-id="element-45">
          {bono.icon}
        </div>}
    </div>

    <div className="p-8 pt-6 flex-1 flex flex-col" data-id="element-46">
      <div className="text-brand-orange font-bold text-sm mb-2 uppercase tracking-wider" data-id="element-47">
        Bono #{index + 1}
      </div>
      <h3 className="font-heading text-2xl text-brand-navy mb-3 pr-8" data-id="element-48">
        {bono.title}
      </h3>
      <div className="flex items-center justify-between mt-auto pt-6 border-t border-slate-100" data-id="element-49">
        <span className="text-slate-500 font-medium" data-id="element-50">{bono.pages}</span>
        <div className="text-right" data-id="element-51">
          <span className="text-xs text-slate-400 block" data-id="element-52">Valor normal</span>
          <span className="text-slate-400 line-through font-bold" data-id="element-53">
            {bono.value}
          </span>
        </div>
      </div>
    </div>
  </div>;
export const Bonuses = () => {
  const [page, setPage] = useState(0);
  const [perView, setPerView] = useState(3);
  useEffect(() => {
    const update = () => {
      if (window.innerWidth < 768) setPerView(1);else if (window.innerWidth < 1024) setPerView(2);else setPerView(3);
    };
    update();
    window.addEventListener('resize', update, {
      passive: true
    });
    return () => window.removeEventListener('resize', update);
  }, []);
  const totalPages = Math.ceil(bonuses.length / perView);
  useEffect(() => {
    if (page > totalPages - 1) setPage(0);
  }, [perView, totalPages, page]);
  const next = useCallback(() => setPage(p => (p + 1) % totalPages), [totalPages]);
  const prev = useCallback(() => setPage(p => (p - 1 + totalPages) % totalPages), [totalPages]);
  // Auto-advance
  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);
  const start = page * perView;
  const visible = bonuses.slice(start, start + perView);
  return <section className="py-20 bg-brand-gray overflow-hidden" data-id="element-54">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-55">
        <SectionHeading subtitle="Llevando el kit hoy, te regalamos estos 6 bonos exclusivos." data-id="element-56">
          Bonos de Regalo
        </SectionHeading>

        <div className="relative" data-id="element-57">
          {/* Arrows */}
          <button onClick={prev} aria-label="Bono anterior" className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-5 z-20 bg-white text-brand-navy w-12 h-12 rounded-full shadow-lg flex items-center justify-center hover:bg-brand-pink hover:text-white transition-colors" data-id="element-58">
            <ChevronLeft className="w-6 h-6" data-id="element-59" />
          </button>
          <button onClick={next} aria-label="Siguiente bono" className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-5 z-20 bg-white text-brand-navy w-12 h-12 rounded-full shadow-lg flex items-center justify-center hover:bg-brand-pink hover:text-white transition-colors" data-id="element-60">
            <ChevronRight className="w-6 h-6" data-id="element-61" />
          </button>

          {/* Slides */}
          <div className="overflow-hidden px-2 min-h-[460px]" data-id="element-62">
            <AnimatePresence mode="wait" data-id="element-63">
              <motion.div key={page} initial={{
              opacity: 0,
              x: 40
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -40
            }} transition={{
              duration: 0.35
            }} className="grid gap-8" style={{
              gridTemplateColumns: `repeat(${perView}, minmax(0, 1fr))`
            }} data-id="element-64">
                {visible.map((bono, i) => <BonusCard key={start + i} bono={bono} index={start + i} data-id="element-65" />)}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-8" data-id="element-66">
            {Array.from({
            length: totalPages
          }).map((_, i) => <button key={i} onClick={() => setPage(i)} aria-label={`Ir al grupo ${i + 1}`} className={`h-3 rounded-full transition-all ${i === page ? 'w-8 bg-brand-pink' : 'w-3 bg-slate-300 hover:bg-slate-400'}`} data-id="element-67" />)}
          </div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mt-16 flex justify-center" data-id="element-68">
          <img src="https://cdn.shopify.com/s/files/1/0999/7487/3381/files/ChatGPT_Image_4_jun_2026_15_11_06.png?v=1780596722&width=1200&quality=80&format=webp" alt="Bonos de regalo" width="1200" height="600" loading="lazy" decoding="async" className="w-full max-w-4xl h-auto drop-shadow-2xl" data-id="element-69" />
        </motion.div>
      </div>
    </section>;
};